/**
 * Control Bridge personal-layer hi-fi view models.
 * Contract extracted from design_handoff_control_bridge/source/*.dc.html Component blocks.
 * @see docs/Krysalis Design System/Control bridge surfaces personal use case/design_handoff_control_bridge/README.md
 */

import type { ControlBridgeQueueItem } from '../deriveViewModel';

export type PersonalOverlayMode = 'system' | 'me';

export type GuidanceNodeStatus = 'done' | 'active' | 'todo';

export type GuidanceNodeType = 'session' | 'waypoint' | 'improvement' | 'activity';

/** Guidance Gantt density — traditional hi-fi bars vs compact summary-bars vs network (8b). */
export type GuidanceViewMode = 'traditional' | 'compact' | 'network';

/** Week-index range on the timeline axis (s=start, e=early finish, l=late finish). */
export interface GuidanceWeekRange {
  s: number;
  e: number;
  l: number;
}

export interface GuidanceRollupCounts {
  done: number;
  open: number;
  eligible: number;
}

export interface GuidanceTreeNode {
  id: string;
  type: GuidanceNodeType;
  label: string;
  /** Company artifact instance id when row maps to a domain/subdomain scope. */
  filterScope?: string;
  status: GuidanceNodeStatus;
  /**
   * Raw queue / SM status phrase (e.g. created, drafted, active).
   * Guidance `status` collapses several phrases to todo/active/done — keep this for Start visibility.
   */
  statusPhrase?: string;
  /** Improvement: open child activity count from queue inference (may exceed attached children). */
  openActivities?: number;
  range: GuidanceWeekRange;
  /** Progress 0–1 for active bars. */
  pct?: number;
  /** Activity assignee display name. */
  res?: string;
  /** Activity-only dependency id. */
  dep?: string;
  /** Waypoint artifact attainment label (mono). */
  artifact?: string;
  /** Server float on a waypoint. Absent when the waypoint has no estimated children. */
  floatWeeks?: number;
  floatUnknown?: boolean;
  /** Plank narrative from bridge card writeback (improvement rows). */
  plankDescription?: string;
  futureCondition?: string;
  resourceAssignment?: string;
  assumptionText?: string;
  estimatedDuration?: string;
  startDate?: string;
  endDate?: string;
  duration?: string;
  critical?: boolean;
  leverage?: boolean;
  blocker?: boolean;
  blockerCount?: number;
  blockerTitle?: string;
  counts?: GuidanceRollupCounts;
  /** True when row is owned by the current user (Me overlay). */
  mine?: boolean;
  children?: GuidanceTreeNode[];
}

export interface GuidanceSummaryStrip {
  whenLabel: string;
  whenRange: string;
  criticalPathWeeks: number;
  leverageCount: number;
  blockerCount: number;
  scopeLabel: string;
}

export interface GuidanceTimelineWindow {
  startWeek: number;
  weekCount: number;
  weekWidthPx?: number;
  todayWeek?: number;
  chartWidthPx?: number;
}

export interface GuidanceFilterHints {
  hideCompletedDefault?: boolean;
  eligibleOnlyDefault?: boolean;
  eligibleActivityCount?: number;
}

/** Server topological layering. The client does not reorder columns. */
export interface GuidanceLayoutHint {
  columns: string[][];
  edges?: Array<[string, string]>;
  endNodeId?: string;
  expectedRange?: string;
}

export interface GuidanceHiFiModel {
  sessionId: string;
  sessionLabel: string;
  objectiveLabel?: string;
  root: GuidanceTreeNode;
  summary: GuidanceSummaryStrip;
  weekCount: number;
  weekWidthPx: number;
  treeColumnWidthPx: number;
  todayWeek?: number;
  timelineWindow?: GuidanceTimelineWindow;
  filterHints?: GuidanceFilterHints;
  layoutHint?: GuidanceLayoutHint;
  /** Scoped attention-queue eligible work for Guidance filters. */
  eligibleWork?: ControlBridgeQueueItem[];
}

export type DriveLifecycle = 'created' | 'drafted' | 'active' | 'closed';

export type DriveItemStatus = 'normal' | 'moved' | 'expected' | 'blocked';

export type DriveDetailLevel = 'summary' | 'token' | 'card';

export type DriveLayoutMode = 'lr' | 'compact';

export type DriveWindowMode = 'today' | 'period' | 'daterange';

export interface DriveReportingPeriodRef {
  ref?: string;
  code?: string;
  start?: string;
  end?: string;
}

export interface DrivePeriodRollup {
  subdomainId: string;
  periodItemId?: string;
  currentState?: string;
  revenueActual?: number;
  costActual?: number;
  netIncome?: number;
  correctionFactor?: number;
  ytdRevenue?: number;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodCode?: string;
}

export interface DriveSubdomainEdge {
  from: string;
  to: string;
  /** Rework / back-edge (dashed amber in design). */
  back?: boolean;
}

export interface DriveGrandchildLane {
  id: string;
  name: string;
}

/** Element-pattern lane mode from kryspace hierarchical Drive manifest. */
export type DriveLaneMode =
  | 'value_component_subdomain'
  | 'type_cluster'
  | 'product_subdomain'
  | 'product_tier'
  | 'per_product'
  | 'per_engagement'
  | 'engagement_lane'
  | 'bridge_domain'
  | 'ledger_substrate';

/** Domain section header grouping typed lanes (seq 60 manifest contract). */
export interface DriveDomainSection {
  id: string;
  name: string;
  domainTypeRef?: string;
  laneMode?: DriveLaneMode | string;
  laneIds?: string[];
  /** Kryspace ledger / substrate sections may set this instead of laneMode. */
  isSubstrate?: boolean;
}

export interface DriveSubdomainNode {
  id: string;
  name: string;
  grandchildren?: DriveGrandchildLane[];
  grandEdges?: DriveSubdomainEdge[];
  laneMode?: DriveLaneMode | string;
  domainSectionId?: string;
  domainSectionTypeRef?: string;
  parentProductLaneId?: string;
  clusterTypeRef?: string;
  productSubdomainTypeRef?: string;
  tierLaneIds?: string[];
  entropyClass?: 'computed' | 'dormant' | 'undefined';
  entropyScore?: number;
  stateCounts?: Record<string, number>;
  /** Period rollup metrics from subdomain reporting-period container (manifest). */
  revenueActual?: number;
  costActual?: number;
  netIncome?: number;
  correctionFactor?: number;
  ytdRevenue?: number;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodCode?: string;
}

export interface DriveFilterHints {
  defaultDetailLevel?: 'summary' | 'token' | 'detailed' | 'card';
}

export interface DriveArtifactItem {
  id: string;
  title: string;
  sub: string;
  /** Subdomain scope id the artifact flows through (phase-1 proxy). */
  subdomainId?: string;
  /** Manifest / queue scope key (may differ from subdomainId for grandchild lanes). */
  filterScope?: string;
  life: DriveLifecycle;
  owner: string;
  status: DriveItemStatus;
  lane?: string;
  cause?: string;
  movedAgo?: string;
  expTo?: string;
  expWhen?: string;
  projectedTo?: string;
  mine?: boolean;
  /** ISO date for window filtering (queue status date). */
  statusDate?: string;
  reportingPeriodRef?: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  /** Parent company artifact when known (ca-*). */
  companyArtifactRef?: string;
  /** Resource filter token (same plane as queue ``res``). */
  res?: string;
  assignedResourceRef?: string;
  /** BSC tracked target — used to place root-scoped cargo onto structure lanes. */
  trackedArtifactRef?: string;
}

export interface DriveSummaryStrip {
  inFlow: number;
  movedRecently: number;
  blocked: number;
  expectedToMove: number;
}

/** CB-DOM-009/013 — catalog Layer A monitor stage on Drive. */
/** One stage from the hosted flow envelope, without item ids. */
export interface DriveOrderFlowStage {
  sequence: number;
  label: string;
  kind?: string;
  wip: number;
}

export interface DriveLaneMonitorStage {
  monitorId: string;
  label: string;
  kind?: string;
  laneKey?: string;
  subdomainTypeRef?: string;
  layerAFlowRef?: string;
  layerAStepRef?: string;
  layerAStepPresent?: boolean;
  mappedItemCount?: number;
  demandChannels?: Array<{
    itemId?: string;
    productPatternRef?: string;
    label?: string;
    channels?: string[];
    source?: string;
  }>;
  demandChannelSource?: string;
  /** CB-DOM-013 — per-period website/email/phone counters (Ops props or derived). */
  demandChannelPeriodCounters?: {
    periodRef?: string;
    periodCode?: string;
    periodStart?: string;
    periodEnd?: string;
    source?: 'ops_reporting' | 'derived' | 'mixed' | string;
    sources?: Record<string, string>;
    counts?: {
      website?: number;
      email?: number;
      phone?: number;
      [key: string]: number | undefined;
    };
    storefrontCount?: number;
    storeCount?: number;
  };
}

export interface DriveHiFiModel {
  domainLabel: string;
  subdomains: DriveSubdomainNode[];
  edges: DriveSubdomainEdge[];
  artifacts: DriveArtifactItem[];
  summary: DriveSummaryStrip;
  /** Hierarchical domain sections with typed lane grouping (kryspace seq 60). */
  sections?: DriveDomainSection[];
  filterHints?: DriveFilterHints;
  stateColorTokens?: Record<string, string>;
  zoomLevels?: Array<'summary' | 'token' | 'detailed' | 'card'>;
  periodRollups?: DrivePeriodRollup[];
  activeReportingPeriod?: DriveReportingPeriodRef;
  /** Eight business lane monitor checkpoints (CB-DOM-009). */
  laneMonitorStages?: DriveLaneMonitorStage[];
  /**
   * Company order stages already aggregated by the flow envelope.
   * Labels and WIP only. Drive lanes render these; they do not walk orders.
   */
  orderFlowStages?: DriveOrderFlowStage[];
  /** Orlin R10 — Drive binds only to the Operational bridge session. */
  boundSessionKind?: 'operational' | 'development' | 'transitional';
  hasOperationalSession?: boolean;
  boundBridgeSessionRef?: string | null;
  emptyReason?: string | null;
}

export interface PersonalLayerViewState {
  mode: PersonalOverlayMode;
  guidance: {
    collapsed: Record<string, boolean>;
    showProjected: boolean;
    showCommitted: boolean;
  };
  drive: {
    lod: DriveDetailLevel;
    layout: DriveLayoutMode;
    grand: boolean;
    projected: boolean;
    win: DriveWindowMode;
    periodDiv: 'month' | 'quarter' | 'year';
    periodOff: number;
    dStart: string;
    dEnd: string;
  };
}

export const DEFAULT_PERSONAL_LAYER_VIEW_STATE: PersonalLayerViewState = {
  mode: 'system',
  guidance: {
    collapsed: {},
    showProjected: true,
    showCommitted: true,
  },
  drive: {
    lod: 'card',
    layout: 'lr',
    grand: false,
    projected: false,
    win: 'today',
    periodDiv: 'quarter',
    periodOff: 0,
    dStart: '',
    dEnd: '',
  },
};
